import { passkeyKekFromPrf, wrapMasterBits, unwrapMasterBits, importMasterKeyFromBits } from './crypto';

// Passkey als dritter Weg zum Entsperren, zusaetzlich zu Passwort und 12 Woertern.
// Die WebAuthn-Erweiterung "prf" liefert je Passkey und Salz 32 geheime Bytes, die nur das Geraet
// (Fingerabdruck, Gesichtserkennung, Windows Hello, Sicherheitsschluessel) erzeugen kann. Daraus
// entsteht ein Schluessel, der eine eigene Huelle des Hauptschluessels oeffnet. Der Server sieht
// nur Credential-ID, Salz und Huelle.

const API = '/api/tresor/passkeys';
const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
const fromB64url = (s) => {
  const b = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b + '='.repeat((4 - (b.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};
const rand = (n) => globalThis.crypto.getRandomValues(new Uint8Array(n));

/** Kann dieser Browser Passkeys mit PRF? Ohne PRF gibt es kein Geheimnis fuer die Huelle. */
export async function passkeySupported() {
  if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) return false;
  try {
    if (typeof PublicKeyCredential.getClientCapabilities === 'function') {
      const caps = await PublicKeyCredential.getClientCapabilities();
      if (caps && 'extension:prf' in caps) return !!caps['extension:prf'];
    }
  } catch (_) { /* ältere Browser: unten weiter */ }
  // Ohne Abfrage-Schnittstelle: nur ein Versuch zeigt es. Anbieten, beim Einrichten sauber scheitern.
  return true;
}

async function prfSecretFromGet(credentialIds, saltById) {
  const evalByCredential = {};
  for (const [id, salt] of Object.entries(saltById)) evalByCredential[id] = { first: fromB64url(salt) };
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: rand(32), // der Server prueft keine Signatur; die Huelle selbst ist der Beweis
      rpId: window.location.hostname,
      allowCredentials: credentialIds.map((id) => ({ type: 'public-key', id: fromB64url(id) })),
      userVerification: 'required',
      timeout: 60000,
      extensions: { prf: { evalByCredential } }
    }
  });
  const secret = assertion?.getClientExtensionResults?.()?.prf?.results?.first;
  if (!secret) {
    const err = new Error('Dieser Passkey liefert kein Geheimnis zum Entsperren (PRF fehlt).');
    err.code = 'PRF_MISSING';
    throw err;
  }
  return { credentialId: b64url(assertion.rawId), secret: new Uint8Array(secret) };
}

/**
 * Passkey einrichten. masterBits sind die rohen Schluesselbytes, die der Aufrufer mit dem Passwort
 * gerade aus der Passwort-Huelle geholt hat; sie werden hier nicht aufbewahrt.
 */
export async function registerPasskey(authenticatedFetch, { masterBits, email, label }) {
  const prfSalt = rand(32);
  const cred = await navigator.credentials.create({
    publicKey: {
      rp: { id: window.location.hostname, name: 'Datenputzer Tresor' },
      user: { id: rand(16), name: email || 'Tresor', displayName: email || 'Datenputzer Tresor' },
      challenge: rand(32),
      pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
      authenticatorSelection: { residentKey: 'preferred', userVerification: 'required' },
      timeout: 60000,
      extensions: { prf: { eval: { first: prfSalt } } }
    }
  });
  const credentialId = b64url(cred.rawId);
  const ext = cred.getClientExtensionResults?.()?.prf;
  if (ext && ext.enabled === false) {
    const err = new Error('Dieser Passkey kann den Tresor nicht entsperren (PRF wird nicht unterstützt).');
    err.code = 'PRF_UNSUPPORTED';
    throw err;
  }
  // Manche Geraete liefern das Geheimnis schon beim Anlegen, andere erst beim ersten Anmelden.
  let secret = ext?.results?.first ? new Uint8Array(ext.results.first) : null;
  if (!secret) ({ secret } = await prfSecretFromGet([credentialId], { [credentialId]: b64url(prfSalt) }));
  try {
    const kek = await passkeyKekFromPrf(secret);
    const { wrappedHex, ivHex } = await wrapMasterBits(masterBits, kek);
    const res = await authenticatedFetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credentialId, prfSalt: b64url(prfSalt), wrapped: wrappedHex, iv: ivHex, label })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Der Passkey konnte nicht gespeichert werden.');
    }
  } finally {
    secret.fill(0);
  }
}

/** Mit Passkey entsperren. Liefert den Hauptschluessel (samt Schluesselbund) oder wirft. */
export async function unlockWithPasskey(authenticatedFetch) {
  const res = await authenticatedFetch(`${API}/material`);
  if (res.status === 429) throw new Error('Zu viele Versuche. Bitte kurz warten.');
  if (!res.ok) throw new Error('Passkey-Daten konnten nicht geladen werden.');
  const { passkeys = [] } = await res.json();
  if (!passkeys.length) throw new Error('Für diesen Tresor ist kein Passkey eingerichtet.');
  const salts = Object.fromEntries(passkeys.map((p) => [p.credentialId, p.prfSalt]));
  const { credentialId, secret } = await prfSecretFromGet(passkeys.map((p) => p.credentialId), salts);
  const entry = passkeys.find((p) => p.credentialId === credentialId);
  if (!entry) throw new Error('Unbekannter Passkey.');
  try {
    const kek = await passkeyKekFromPrf(secret);
    const bits = await unwrapMasterBits(entry.wrapped, entry.iv, kek);
    const key = await importMasterKeyFromBits(bits);
    bits.fill(0);
    authenticatedFetch(`${API}/${entry.id}/used`, { method: 'POST' }).catch(() => {});
    return key;
  } finally {
    secret.fill(0);
  }
}
